import { t } from '@trimble/i18n';
import type {
  FileFolderItem,
  FxBreakline,
  FxBreaklineType,
  FxWorkspace,
  FxWorkspaceJob,
  FxJobStatus,
  MissionItem,
  PointCloudItem,
  TrajectoryItem,
} from '@mobile-mapping/providers';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';
import { UnreachableCaseError } from 'ts-essentials';
import { adaptProjectDataNodesForFxReferences } from './adapt-fx-reference-nodes';
import type {
  FxWorkspaceTreeItem,
  FxWorkspaceTreeItemKind,
} from './fx-workspace-tree.types';
import {
  buildProjectDataCategoryNodes,
  getProjectDataCategoryFolderProps,
  PROJECT_DATA_TREE_CATEGORIES,
} from './project-data-tree-nodes';

export type { FxWorkspaceTreeItem, FxWorkspaceTreeItemKind };

const EXTRACTED_FEATURES_FOLDER_SORT_PRIORITY = 1;
const REFERENCES_FOLDER_SORT_PRIORITY = 2;
const PCI_IN_EXTRACTED_FEATURES_SORT_PRIORITY = 1;
const BREAKLINES_IN_EXTRACTED_FEATURES_SORT_PRIORITY = 2;
const JOB_IN_EXTRACTED_FEATURES_SORT_PRIORITY = 3;

const EXTRACTED_FEATURES_TREE_ICON = 'tc-icon-layers' as const;

export interface FxWorkspaceTreeBuildOptions {
  getWorkspaceBreaklines?(workspaceId: string): readonly FxBreakline[];
  isBreaklinesBuilding?(workspaceId: string): boolean;
}

function resolveWorkspaceBreaklines(
  workspaceId: string,
  getWorkspaceBreaklines?: (workspaceId: string) => readonly FxBreakline[]
): readonly FxBreakline[] {
  return getWorkspaceBreaklines?.(workspaceId) ?? [];
}

export interface FxWorkspaceProjectData {
  readonly missions: readonly MissionItem[];
  readonly trajectories: readonly TrajectoryItem[];
  readonly models: readonly FileFolderItem[];
  readonly pointClouds: readonly PointCloudItem[];
}

function getJobStatusLabel(status: FxJobStatus): string {
  switch (status) {
    case 'processing':
      return t('mobileMapping.fxWorkspace.jobStatus.processing');
    case 'complete':
      return t('mobileMapping.fxWorkspace.jobStatus.complete');
    case 'failed':
      return t('mobileMapping.fxWorkspace.jobStatus.failed');
    default:
      throw new UnreachableCaseError(status);
  }
}

function getBreaklineTypeLabel(type: FxBreaklineType): string {
  switch (type) {
    case 'curbAndGutter':
      return t('mobileMapping.fxWorkspace.breaklines.curbAndGutter');
    case 'roadCrown':
      return t('mobileMapping.fxWorkspace.breaklines.roadCrown');
    case 'jerseyBarrier':
      return t('mobileMapping.fxWorkspace.breaklines.jerseyBarrier');
    default:
      throw new UnreachableCaseError(type);
  }
}

function referencesFolderId(workspaceId: string): string {
  return `${workspaceId}::references`;
}

export function extractedFeaturesFolderId(workspaceId: string): string {
  return `${workspaceId}::extractedFeatures`;
}

export function breaklinesFolderId(workspaceId: string): string {
  return `${workspaceId}::extractedFeatures::breaklines`;
}

export function pciFolderId(workspaceId: string): string {
  return `${workspaceId}::extractedFeatures::pci`;
}

function breaklineTypeNodeId(
  workspaceId: string,
  type: FxBreaklineType
): string {
  return `${workspaceId}::extractedFeatures::breaklines::${type}`;
}

function jobItemId(workspaceId: string, jobId: string): string {
  return `${workspaceId}::extractedFeatures::${jobId}`;
}

export function isExtractedFeatureTreeNodeKind(
  kind: FxWorkspaceTreeItemKind | undefined
): kind is
  | 'extractedFeatures'
  | 'pci'
  | 'breaklines'
  | 'breaklineType'
  | 'job' {
  return (
    kind === 'extractedFeatures' ||
    kind === 'pci' ||
    kind === 'breaklines' ||
    kind === 'breaklineType' ||
    kind === 'job'
  );
}

function referenceCategoryId(
  workspaceId: string,
  category: (typeof PROJECT_DATA_TREE_CATEGORIES)[number]
): string {
  return `${workspaceId}::references::${category}`;
}

function getWorkspaceJobs(workspace: FxWorkspace): readonly FxWorkspaceJob[] {
  return workspace.jobs;
}

function buildPciFolderNode(
  workspaceId: string
): TreeNodeDefinition<FxWorkspaceTreeItem> {
  return {
    id: pciFolderId(workspaceId),
    label: t('mobileMapping.fxWorkspace.pci.title'),
    labelIcon: EXTRACTED_FEATURES_TREE_ICON,
    parentId: extractedFeaturesFolderId(workspaceId),
    isLeaf: false,
    data: { kind: 'pci', workspaceId },
    showVisibilityToggle: true,
    sortPriority: PCI_IN_EXTRACTED_FEATURES_SORT_PRIORITY,
  };
}

function buildReferenceNodes(
  workspaceId: string,
  projectData: FxWorkspaceProjectData
): TreeNodeDefinition<FxWorkspaceTreeItem>[] {
  const nodes: TreeNodeDefinition<FxWorkspaceTreeItem>[] = [
    {
      id: referencesFolderId(workspaceId),
      label: t('mobileMapping.fxWorkspace.references.title'),
      labelIcon: 'tc-icon-folder',
      parentId: workspaceId,
      isLeaf: false,
      data: { kind: 'references', workspaceId },
      showVisibilityToggle: false,
      sortPriority: REFERENCES_FOLDER_SORT_PRIORITY,
    },
  ];

  for (const category of PROJECT_DATA_TREE_CATEGORIES) {
    const projectDataNodes = buildProjectDataCategoryNodes(
      category,
      projectData
    );
    const categoryNodes = adaptProjectDataNodesForFxReferences(
      workspaceId,
      category,
      projectDataNodes
    );
    const categoryId = referenceCategoryId(workspaceId, category);

    nodes.push({
      id: categoryId,
      ...getProjectDataCategoryFolderProps(category),
      parentId: referencesFolderId(workspaceId),
      isLeaf: categoryNodes.length === 0,
      data: { kind: 'referenceCategory', workspaceId, category },
      showVisibilityToggle: false,
    });

    nodes.push(...categoryNodes);
  }

  return nodes;
}

function buildBreaklinesNodes(
  workspaceId: string,
  breaklines: readonly FxBreakline[],
  isBuilding: boolean
): TreeNodeDefinition<FxWorkspaceTreeItem>[] {
  const nodes: TreeNodeDefinition<FxWorkspaceTreeItem>[] = [
    {
      id: breaklinesFolderId(workspaceId),
      label: t('mobileMapping.fxWorkspace.breaklines.title'),
      labelIcon: EXTRACTED_FEATURES_TREE_ICON,
      parentId: extractedFeaturesFolderId(workspaceId),
      isLeaf: breaklines.length === 0,
      data: { kind: 'breaklines', workspaceId },
      showVisibilityToggle: true,
      sortPriority: BREAKLINES_IN_EXTRACTED_FEATURES_SORT_PRIORITY,
      ...(isBuilding
        ? {
            status: {
              status: 'processing' as const,
              message: t('mobileMapping.fxWorkspace.breaklines.building'),
            },
          }
        : {}),
    },
  ];

  for (const breakline of breaklines) {
    nodes.push({
      id: breaklineTypeNodeId(workspaceId, breakline.type),
      label: getBreaklineTypeLabel(breakline.type),
      parentId: breaklinesFolderId(workspaceId),
      isLeaf: true,
      data: {
        kind: 'breaklineType',
        workspaceId,
        breaklineType: breakline.type,
      },
      showVisibilityToggle: true,
    });
  }

  return nodes;
}

function buildExtractedFeatureNodes(
  workspace: FxWorkspace,
  breaklines: readonly FxBreakline[],
  isBreaklinesBuilding?: (workspaceId: string) => boolean
): TreeNodeDefinition<FxWorkspaceTreeItem>[] {
  const jobs = getWorkspaceJobs(workspace);
  const workspaceId = workspace.id;
  const nodes: TreeNodeDefinition<FxWorkspaceTreeItem>[] = [
    {
      id: extractedFeaturesFolderId(workspaceId),
      label: t('mobileMapping.fxWorkspace.extractedFeatures.title'),
      labelIcon: EXTRACTED_FEATURES_TREE_ICON,
      parentId: workspaceId,
      isLeaf: false,
      data: { kind: 'extractedFeatures', workspaceId },
      showVisibilityToggle: true,
      sortPriority: EXTRACTED_FEATURES_FOLDER_SORT_PRIORITY,
    },
  ];

  nodes.push(buildPciFolderNode(workspaceId));
  nodes.push(
    ...buildBreaklinesNodes(
      workspaceId,
      breaklines,
      isBreaklinesBuilding?.(workspaceId) ?? false
    )
  );

  for (const job of jobs) {
    nodes.push({
      id: jobItemId(workspaceId, job.id),
      label: job.name,
      parentId: extractedFeaturesFolderId(workspaceId),
      isLeaf: true,
      data: {
        kind: 'job',
        workspaceId,
        jobId: job.id,
      },
      showVisibilityToggle: true,
      sortPriority: JOB_IN_EXTRACTED_FEATURES_SORT_PRIORITY,
      visibleContent: () => (
        <div class="flex items-center gap-2 pr-2 text-xs text-base-muted">
          <span>{getJobStatusLabel(job.status)}</span>
        </div>
      ),
    });
  }

  return nodes;
}

export const buildFxWorkspaceNodesForActive = (
  workspace: FxWorkspace,
  projectData: FxWorkspaceProjectData,
  options?: FxWorkspaceTreeBuildOptions
): TreeNodeDefinition<FxWorkspaceTreeItem>[] => {
  const workspaceId = workspace.id;
  const nodes = [
    ...buildExtractedFeatureNodes(
      workspace,
      resolveWorkspaceBreaklines(workspaceId, options?.getWorkspaceBreaklines),
      options?.isBreaklinesBuilding
    ),
    ...buildReferenceNodes(workspaceId, projectData),
  ];

  return nodes.map((node) =>
    node.parentId === workspaceId ? { ...node, parentId: undefined } : node
  );
};
